import type { ApiErrorResponse, ApiFieldError } from '@usc/shared';
import { config } from '../config';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fieldErrors: ApiFieldError[] = [],
    public readonly traceId?: string,
  ) {
    super(message);
  }
}

export interface ApiContext {
  token?: string;
  tenantId?: string;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, ctx: ApiContext, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (ctx.token) headers['Authorization'] = `Bearer ${ctx.token}`;
  if (ctx.tenantId) headers['X-Tenant-Id'] = ctx.tenantId;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    signal: options.signal,
  });

  if (!res.ok) {
    let payload: ApiErrorResponse | undefined;
    try {
      payload = (await res.json()) as ApiErrorResponse;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(
      res.status,
      payload?.code ?? 'INTERNAL_ERROR',
      payload?.message ?? `Request failed with status ${res.status}`,
      payload?.errors ?? [],
      payload?.traceId,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Authenticated binary download (blob + filename from the disposition header). */
export async function apiDownload(path: string, ctx: ApiContext): Promise<void> {
  const headers: Record<string, string> = {};
  if (ctx.token) headers['Authorization'] = `Bearer ${ctx.token}`;
  if (ctx.tenantId) headers['X-Tenant-Id'] = ctx.tenantId;
  const res = await fetch(`${config.apiBaseUrl}${path}`, { headers });
  if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'Download failed');
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = match?.[1] ?? 'download';
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Authenticated fetch returning the raw blob (for inline preview). */
export async function apiBlob(path: string, ctx: ApiContext): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (ctx.token) headers['Authorization'] = `Bearer ${ctx.token}`;
  if (ctx.tenantId) headers['X-Tenant-Id'] = ctx.tenantId;
  const res = await fetch(`${config.apiBaseUrl}${path}`, { headers });
  if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'Download failed');
  return res.blob();
}
